import { request, toProblem, unwrapData } from './base';
import type { ApiResponse } from './types';

/**
 * Interface for a What's New entry
 */
export interface WhatsNewEntry {
  id: string;
  title: string;
  description: string;
  published_at: string;
  image?: string;
  image_mimetype?: string;
  is_viewed: boolean;
}

/**
 * Get all What's New entries with viewed status
 */
export interface WhatsNewListResult {
  entries: WhatsNewEntry[];
  meta?: ApiResponse<unknown>['meta'];
}

export interface MessageResult {
  message: string;
  meta?: ApiResponse<unknown>['meta'];
}

export interface UnseenCountResult {
  unseenCount: number;
  meta?: ApiResponse<unknown>['meta'];
}

export const getWhatsNewEntries = async (): Promise<WhatsNewListResult> => {
  try {
    const response = await request<{ entries: WhatsNewEntry[] }>('/whatsnew/');
    const data = unwrapData(response);
    return {
      entries: data.entries ?? [],
      meta: response.meta,
    };
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Mark one or more What's New entries as viewed
 */
export const markWhatsNewViewed = async (
  entryIds: string[] = [],
  markAll = false
): Promise<MessageResult> => {
  try {
    const response = await request<{ message: string; created: number }>('/whatsnew/mark-viewed/', {
      method: 'POST',
      body: JSON.stringify({ entry_ids: entryIds, mark_all: markAll }),
    });

    const data = unwrapData(response);
    return {
      message: data.message,
      meta: response.meta,
    };
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Get the count of unseen What's New entries
 */
export const getUnseenWhatsNewCount = async (): Promise<UnseenCountResult> => {
  try {
    const response = await request<{ unseenCount: number }>('/whatsnew/unseen-count/');
    const data = unwrapData(response);
    return {
      unseenCount: data.unseenCount,
      meta: response.meta,
    };
  } catch (error) {
    throw toProblem(error);
  }
};
